import { getLocale, t } from './i18n.js'
import type { Activity, VenueView } from '@sorted/types'

export function activityLabel(a: Activity): string {
  return t(`activity.${a}`)
}

/**
 * Emoji, only for WhatsApp.
 *
 * The UI uses Lucide icons, but a WhatsApp message is plain text in someone
 * else's app - there is nowhere to render an SVG, and a bare line reads as
 * spam. This is the one place an emoji is the right tool.
 */
const WHATSAPP_EMOJI: Record<Activity, string> = {
  coffee: '☕',
  brunch: '🥐',
  drinks: '🍸',
  dinner: '🍽️',
  walk: '🌳',
  beer_garden: '🍺',
}

/**
 * How fair the trip is, in words rather than minutes.
 *
 * The ETAs behind this are transit-only, so printing "40 min / 27 min" is
 * precise about something we do not actually know: drive or cycle and both
 * numbers are wrong, and the gap between them reads as unfairness that may be
 * an artifact of the travel mode. Spec §7 wants the fairness *proof* on the
 * card - this keeps the claim and drops the false precision.
 *
 * `viewerIsCreator` decides which side "closer to you" refers to.
 *
 * A null creator ETA means the meeting is pinned to an area and she never
 * gave an origin. There is no fairness to claim then - only one person is
 * travelling - so the line becomes that person's own time. Minutes are
 * defensible here in a way the comparison is not: one number carries no
 * implicit claim that the two sides are balanced.
 */
export function fairnessLabel(
  etaCreatorS: number | null,
  etaRecipientS: number | null,
  viewerIsCreator: boolean,
): string {
  // Nobody was routed - a pinned-area meeting. There is no travel time to state
  // for either side, so the line says where the place is rather than
  // manufacturing a number.
  if (etaCreatorS === null && etaRecipientS === null) return t('fair.chosenArea')

  if (etaCreatorS === null) {
    return viewerIsCreator
      ? t('fair.chosenArea')
      : t('fair.yourTrip', { minutes: String(Math.round(etaRecipientS! / 60)) })
  }

  if (etaRecipientS === null) {
    return viewerIsCreator
      ? t('fair.yourTrip', { minutes: String(Math.round(etaCreatorS / 60)) })
      : t('fair.chosenArea')
  }

  const mine = viewerIsCreator ? etaCreatorS : etaRecipientS
  const theirs = viewerIsCreator ? etaRecipientS : etaCreatorS

  const longer = Math.max(mine, theirs)
  const gap = Math.abs(mine - theirs)

  // Within a quarter of the longer trip is not a difference anyone notices,
  // and is well inside the error of a single-mode estimate.
  if (longer === 0 || gap / longer < 0.25) return t('fair.balanced')

  return t(mine < theirs ? 'fair.slightlyYou' : 'fair.slightlyThem')
}

/**
 * Is this datetime-local value in the future?
 *
 * A datetime-local input's own `min` constrains the picker UI only - a typed
 * or pasted value sails straight past it - so the check has to exist in code
 * as well. Without it the server rejects the meeting three taps later, after
 * the location has already been shared, and the reported failure was a bare
 * 400 with no explanation.
 */
export function isFutureLocal(local: string): boolean {
  if (!local) return false
  const t = new Date(local).getTime()
  return Number.isFinite(t) && t > Date.now()
}

export function minutes(seconds: number): string {
  return `${Math.max(1, Math.round(seconds / 60))} min`
}

/** "Thu 11:00" - short, because the card has one job. */
export function meetingTime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString(getLocale(), {
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function priceLabel(level: number | null): string {
  return level ? '€'.repeat(level) : ''
}

/**
 * Rating and count together, never apart - a bare "4.5" is what sends people
 * to Google Maps to check whether it means anything (build-spec §7b).
 */
export function ratingLabel(v: Pick<VenueView, 'rating' | 'reviewCount'>): string | null {
  if (v.rating === null) return null
  return `${v.rating.toFixed(1)} (${v.reviewCount.toLocaleString(getLocale())})`
}

/**
 * Google Maps deep link - unfurls a venue card in WhatsApp, free (§9).
 *
 * `place_id` is the durable identifier and is explicitly exempt from Google's
 * caching restriction (§8), so linking by it costs nothing and always lands on
 * the right place.
 *
 * `query` is the fallback Maps shows if the id ever fails to resolve, so it
 * should be the venue name - the previous literal "Google" would have sent
 * someone to a search for the word "Google".
 */
export function mapsUrl(placeId: string, name: string): string {
  // `name` is REQUIRED, not optional. Making it optional shipped a WhatsApp
  // message with `?query=&query_place_id=...` - Maps treats the empty query as
  // "search for nothing", opens the whole city with no pin, and WhatsApp
  // unfurls a generic "Google Maps" card instead of the venue. The one call
  // site that forgot to pass it was the one that gets shared.
  const q = encodeURIComponent(name)
  return `https://www.google.com/maps/search/?api=1&query=${q}&query_place_id=${placeId}`
}

/**
 * The venue's own page on Google Maps, where the reviews are.
 *
 * Distinct from `mapsUrl`, which is a SEARCH for the place and is what a
 * shared link should be: it unfurls a venue card in WhatsApp and survives a
 * place id that no longer resolves, falling back to the name. This one goes
 * straight to the place itself, which is what someone tapping a rating wants.
 *
 * There is no documented deep link to the reviews TAB - the old
 * `search.google.com/local/reviews` form now 404s - so this opens the place
 * page, where the rating and reviews are the first thing below the photos.
 */
export function reviewsUrl(placeId: string): string {
  return `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(placeId)}`
}

/**
 * A plain web search for the venue.
 *
 * Maps answers "where and is it good"; a web search answers "what is this
 * place actually like" - menu, photos, whether it is someone's local. Both
 * are one tap from the card, because the §14 gate's whole question is whether
 * a suggestion is trustworthy, and letting someone check is cheaper than
 * making them leave to do it.
 */
export function searchUrl(name: string): string {
  return `https://www.google.com/search?q=${encodeURIComponent(name)}`
}

/**
 * When, spelled out in full - for the WhatsApp message.
 *
 * On the card "Mon, 11:00" is unambiguous because you are looking at it now.
 * In a chat it is read later, possibly much later, and "Mon" alone could mean
 * any Monday. A message that ends a negotiation has to carry the date.
 */
export function meetingWhenLong(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString(getLocale(), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Bob's confirmation message - spec §9.
 *
 * OUR link leads, not the Maps one.
 *
 * WhatsApp unfurls only the first url, and the Maps link used to take that
 * slot because it renders a venue card for free. But the two links are not
 * equivalent: the Maps link is a dead pointer at a place, while ours is the
 * meeting itself - it is what the creator opens to see that a decision has
 * been made, and it keeps showing the right thing afterwards. Sending the
 * Maps link as the main one hands over the venue and drops the meeting.
 *
 * Directions are still one tap away, from the confirmed screen our link opens.
 *
 * The decision has to be readable without tapping anything (§9: "plain text
 * delivers the decision with nothing tapped"), so the venue and the full date
 * get their own lines rather than being run together - a wrapped single line
 * in a chat bubble buries exactly the part that matters.
 */
export function whatsappText(
  venue: VenueView,
  activity: Activity,
  meetAt: string,
  shareUrl: string,
): string {
  return [
    `${WHATSAPP_EMOJI[activity]} *${venue.name}*`,
    meetingWhenLong(meetAt),
    '',
    shareUrl,
  ].join('\n')
}

export function whatsappHref(text: string): string {
  return `https://wa.me/?text=${encodeURIComponent(text)}`
}
